import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack/stack";
import { useGetBusiness } from "@/business/query";
import { cn } from "@/core/utils/utils";
import { EDITOR_POLL_INTERVAL } from "@/payroll/editor/constants";
import { EditorConversation } from "@/payroll/editor/editor-conversation";
import { usePayrollEditor } from "@/payroll/editor/query";
import { RulesReview } from "@/payroll/editor/rules-review";
import {
  editorError,
  errorStatus,
  isAccessError,
} from "@/payroll/editor/state";
import { useResizableColumn } from "./use-resizable-column";
import { WorkspaceSkeleton } from "./workspace-skeleton";

// One card on the app ground, the way every other page in the product is built
// — not a full-bleed white slab under a grey strip. Everything inside it is
// flat and divided by hairlines, so there is exactly one surface edge.
const CARD_CLASS =
  "min-h-0 w-full flex-1 overflow-hidden border-stroke-tertiary shadow-none";

export const EditorWorkspace = ({
  businessId,
  userId,
}: {
  businessId: string;
  userId: string;
}) => {
  // A full workbook needs the window. Expanding keeps the conversation as a
  // narrow rail so the owner can still ask about what they are looking at.
  const [tab, setTab] = useState("changes");
  const { width, dragging, separatorProps } = useResizableColumn(
    `payroll-editor:${businessId}:column`,
    400,
  );
  const business = useGetBusiness(businessId, undefined, {
    refetchInterval: EDITOR_POLL_INTERVAL.idle,
  });
  const allowed = business.data?.can_edit_payroll_rules === true;
  const editor = usePayrollEditor(businessId, allowed);
  const { workspaceId } = editor;
  const workspace = editor.messages.data?.workspace ?? editor.current.data;
  const accessError = [
    editor.messages.error,
    editor.current.error,
    editor.documents.error,
    editor.send.error,
    editor.upload.error,
    editor.remove.error,
    editor.open.error,
    editor.publish.error,
    editor.discard.error,
    editor.cancel.error,
  ].find(isAccessError);
  // There is no landing screen and no workspace in the URL. A reader who opens
  // Payroll rules wants the workspace, and the workspace already renders the
  // current rules on its Rules tab, which reads as the plan when nothing has
  // moved. The landing was a second way to read the same document with a door
  // on it, and once you went through the door there was no way back, which is
  // the proof that nothing needed it.
  //
  // Opening is idempotent on the API: an existing draft and its thread are
  // reused, so this creates at most one draft per business, and a draft
  // identical to the published rules reports `has_changes: false`.
  const needsWorkspace =
    allowed && !accessError && !!editor.current.data && !workspaceId;
  useEffect(() => {
    if (!needsWorkspace || editor.open.isPending || editor.open.isError) return;
    editor.open.mutate();
  }, [needsWorkspace, editor.open]);

  if (business.isPending) return <WorkspaceSkeleton width={width} />;
  if (business.isError)
    return (
      <div className="space-y-3 p-6" role="alert">
        <p>{editorError(business.error)}</p>
        <Button size="xs" onClick={() => void business.refetch()}>
          Retry
        </Button>
      </div>
    );
  if (!allowed || accessError)
    return (
      <section className="space-y-3 p-6">
        <h1 className="text-xl font-semibold">Payroll rules</h1>
        <p role="alert">
          {accessError
            ? editorError(accessError)
            : "Payroll rules editing is not available for your account. Contact your business owner or Volca."}
        </p>
        <Button asChild size="xs" variant="tertiary">
          <Link to={errorStatus(accessError) === 401 ? "/login" : "/"}>
            {errorStatus(accessError) === 401 ? "Sign in" : "Return to payroll"}
          </Link>
        </Button>
      </section>
    );

  const readError = editor.messages.error ?? editor.current.error;
  if (!workspace && !readError) return <WorkspaceSkeleton width={width} />;
  if (!workspace || errorStatus(readError) === 404)
    return (
      <section className="space-y-3 p-6">
        <h1 className="text-xl font-semibold">Payroll rules</h1>
        <p role="alert">{editorError(readError)}</p>
        <Button
          size="xs"
          variant="tertiary"
          onClick={() => void editor.current.refetch()}
        >
          Retry
        </Button>
      </section>
    );
  return (
    <Stack
      gap="none"
      fullWidth
      className="h-full min-h-0 bg-background text-sm text-primary-text"
    >
      {!workspaceId ? (
        <Stack
          gap="none"
          align="stretch"
          direction="row"
          className="mx-auto flex min-h-0 w-full max-w-[1680px] flex-1 px-8 pb-6"
        >
          <Card className={cn(CARD_CLASS, "flex flex-col justify-center")}>
            {editor.open.isError ? (
              <div className="space-y-3 p-6" role="alert">
                <p>{editorError(editor.open.error)}</p>
                <Button size="xs" onClick={() => editor.open.reset()}>
                  Try again
                </Button>
              </div>
            ) : (
              <WorkspaceSkeleton width={width} label="Opening payroll rules" />
            )}
          </Card>
        </Stack>
      ) : (
        <div
          // The composer holds a column the reader sizes; everything else is
          // the work pane. One column below `lg`, where there is no room to
          // split. There was also a widen toggle that pinned the conversation
          // to its narrowest; it changed a 400px column to 300px, which is not
          // worth a control, and the thing it was reaching for — covering the
          // app's own nav — it could never do from in here.
          style={{ "--split": `${width}px auto 1fr` } as React.CSSProperties}
          className={cn(
            // The workbench, not the document.
            //
            // Every surface in the app is gray-0 or gray-5, so an editor built
            // from them looks exactly like the thing it edits, and you cannot
            // tell from across the room whether you are reading payroll or
            // changing it. The output pane has to keep the product's own
            // appearance, because it is a faithful preview of the workbook, so
            // the difference lives in the chrome around it: the chrome takes a
            // darker ground and the output sits on it as a white sheet.
            "grid min-h-0 w-full flex-1 grid-cols-1 border-t border-stroke-tertiary bg-gray-10",
            "lg:[grid-template-columns:var(--split)]",
          )}
        >
          <EditorConversation
            key={`conversation:${workspaceId}`}
            editor={editor}
            workspace={workspace}
            businessId={businessId}
            workspaceId={workspaceId}
            storageKey={`payroll-editor:${userId}:${businessId}:${workspaceId}:message`}
          />
          <div
            {...separatorProps}
            // Above everything beside it. The hover band is 13px wide and runs
            // the full height, so wherever a neighbour painted over it the
            // band broke: it showed against the conversation and the artifact,
            // disappeared behind the headers and the roster's pinned columns,
            // and read as three disconnected pieces of one line.
            className="group relative z-30 hidden w-px cursor-col-resize select-none bg-stroke-tertiary focus-visible:outline-none lg:block"
          >
            {/* A 1px line is the right weight and the wrong hit area.

                Neutral, not the interactive green. The band runs the whole
                height of the split, so hovering anywhere on it painted a green
                stripe up through the white tab bar and the control row, which
                read as something happening in the header rather than as a
                column about to be resized. */}
            <span
              aria-hidden
              className={cn(
                "absolute inset-y-0 -left-1.5 -right-1.5 transition-colors",
                // Hover is quieter than the two states that mean something
                // happened. `stroke-secondary` is `gray-30`, and at 60% across
                // a 13px band running the full height of the split it read as
                // a bar appearing beside the conversation rather than as a
                // line you could pick up. Keyboard focus and an actual drag
                // stay at full strength: those are states the reader put the
                // separator into, not ones the pointer passed through.
                "group-hover:bg-stroke-secondary/30 group-focus-visible:bg-stroke-secondary",
                { "bg-stroke-secondary": dragging },
              )}
            />
          </div>
          <RulesReview
            key={`review:${workspaceId}`}
            workspace={workspace}
            businessId={businessId}
            workspaceId={workspaceId}
            tab={tab}
            onTabChange={setTab}
            publish={editor.publish}
            discard={editor.discard}
            busy={editor.messages.data?.isActive ?? false}
          />
        </div>
      )}
    </Stack>
  );
};
